import { useContext, useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";

import { RoutingRule } from "./RoutingRule";
import { LayoutContext } from "../../common/components/layout/LayoutContext";
import { createRoutingRule, deleteRoutingRule, fetchRoutingRules, updateRoutingRule, updateRoutingRulesOrder } from "./routingRulesApi";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import TicketTypeLabel from "../ticketType/TicketTypeLabel";
import DepartmentLabel from "../departments/DepartmentLabel";
import RoutingRuleForm from "./RoutingRuleForm";
import RoutingRuleCategoriesBreadCrumb from "./RoutingRuleCategoriesBreadCrumb";
import { fixedWidthColumnStyle } from "../../common/utils/common";

export default function RoutingRulesPage() {
  const { setFullContentWidth, setStandardContentWidth } = useContext(LayoutContext);
  const [showRoutingRuleDialog, setShowRoutingRuleDialog] = useState(false);
  const [routingRules, setRoutingRules] = useState<RoutingRule[]>();
  const [selectedRoutingRule, setSelectedRoutingRule] = useState<RoutingRule | undefined>(undefined);

  useEffect(() => {
    setFullContentWidth();
    doFetchRoutingRules();
    return () => setStandardContentWidth();
  }, []);

  const doFetchRoutingRules = async () => {
    const rules = await fetchRoutingRules();
    setRoutingRules(rules);
  };

  const closeDialog = () => {
    setSelectedRoutingRule(undefined);
    setShowRoutingRuleDialog(false);
  };

  const onEdit = (routingRule: RoutingRule) => {
    setSelectedRoutingRule(routingRule);
    setShowRoutingRuleDialog(true);
  };

  const onDelete = (routingRule: RoutingRule) => {
    confirmDialog({
      header: "Confirmare stergere",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div className="text-xl">
          Esti sigur ca vrei sa stergi regula de rutare <span className="font-bold">{routingRule.name}</span>?
        </div>
      ),
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: async () => {
        await deleteRoutingRule(routingRule);
        await doFetchRoutingRules();
      },
    });
  };

  const onSubmit = async (routingRule: RoutingRule) => {
    if (selectedRoutingRule) {
      await updateRoutingRule(routingRule);
      setSelectedRoutingRule(undefined);
    } else {
      await createRoutingRule(routingRule);
    }
    await doFetchRoutingRules();
    closeDialog();
  };

  const onRowReorder = async (event: any) => {
    const reorderedRules = event.value as RoutingRule[];
    await updateRoutingRulesOrder(reorderedRules);
    await doFetchRoutingRules();
  };

  return (
    <Card
      title={
        <div className="flex align-items-center justify-content-between">
          <div className="text-2xl font-bold">Reguli rutare</div>
          <Button label="Adauga" icon="pi pi-plus" onClick={() => setShowRoutingRuleDialog(true)} />
        </div>
      }
    >
      <DataTable size="small" reorderableRows value={routingRules} emptyMessage={<NoResultsFound />} onRowReorder={onRowReorder}>
        <Column header=" " className="w-3rem" rowReorder></Column>
        <Column header="Nume" field="name"></Column>
        <Column header="Ordine Executie" field="executionOrder" className="text-center"></Column>
        <Column header="Departament" body={(rowData: RoutingRule) => <DepartmentLabel value={rowData.departmentId} />} />
        <Column header="Tip" body={(rowData: RoutingRule) => <TicketTypeLabel value={rowData.ticketType} />}></Column>
        <Column
          style={fixedWidthColumnStyle(450)}
          header="Categorii"
          body={(rowData: RoutingRule) => <RoutingRuleCategoriesBreadCrumb categories={rowData.categories} />}
        ></Column>
        <Column header="Locatii" body={(rowData: RoutingRule) => rowData.locations?.map((location) => location.name).join(", ")}></Column>
        <Column header="Grupa" field="teamName"></Column>
        <Column
          align="center"
          header="Activa"
          body={(rowData: RoutingRule) => <i className={rowData.isActive ? "pi pi-check text-green-500" : "pi pi-times text-red-500"} />}
        ></Column>
        <Column
          className="w-8rem"
          body={(rowData: RoutingRule) => (
            <div className="flex align-items-center justify-content-end gap-2">
              <Button size="small" icon="pi pi-pencil" rounded text onClick={() => onEdit(rowData)} />
              <Button size="small" severity="danger" icon="pi pi-trash" rounded text onClick={() => onDelete(rowData)} />
            </div>
          )}
        ></Column>
      </DataTable>
      <Dialog
        className="w-almost-full xl:w-8"
        header={selectedRoutingRule ? "Editeaza regula" : "Adauga regula"}
        visible={showRoutingRuleDialog}
        onHide={() => closeDialog()}
      >
        <RoutingRuleForm routingRule={selectedRoutingRule} onSubmit={onSubmit}></RoutingRuleForm>
      </Dialog>
    </Card>
  );
}
